import { AhaEditorUtils, HtmlSerializer, slate } from '@aha-app/aha-editor-v2';
import { randomId } from '@aha-app/mvc';
import { normalizeVerticalAlign } from '../utils/verticalAlign';
const { createEditor } = slate;

// There are three easy ways to test these functions.
//   1) Override the htmlText parameter
//   2) Copy some HTML to your clipboard under the text/html format and paste it
//   3) Copy a table from an Aha! note and paste it
//
// For 1, you can simply override the htmlText parameter below with the HTML you
// want. Here is a sample table you can use.
//
// htmlText = `
//   <table style="border-collapse: collapse; width: 100%;">
//     <tr>
//       <th style="border: 1px solid black; padding: 15px; background-color: #f2f2f2; font-size: 16px;">Header 1</th>
//       <th width="50" style="border: 1px solid black; padding: 15px; background-color: #f2f2f2; font-size: 16px;">Header 2</th>
//       <th style="border: 1px solid black; padding: 15px; background-color: #f2f2f2; font-size: 16px;">Header 3</th>
//     </tr>
//     <tr>
//       <td style="border: 1px solid black; padding: 15px;">Short text</td>
//       <td width="50" style="border: 1px solid black; padding: 15px;">This cell has more content to test how the width adapts to longer text content. Every gesture here was a language: the tilt of a head, the pause before breath, the whisper of sand falling from a robe. Words were dangerous, for they left echoes that even the desert remembered.</td>
//       <td style="border: 1px solid black; padding: 15px; text-align: right;">Right aligned</td>
//       <td>This cell has more content and no cell width to test how the width adapts to longer text content. Every gesture here was a language: the tilt of a head, the pause before breath, the whisper of sand falling from a robe. Words were dangerous, for they left echoes that even the desert remembered.</td>
//     </tr>
//     <tr>
//       <td colspan="2" style="border: 1px solid black; padding: 15px;">This cell spans two columns to test colspan handling</td>
//       <td rowspan="2" style="border: 1px solid black; padding: 15px; vertical-align: middle;">This cell spans two rows to test rowspan handling</td>
//     </tr>
//     <tr>
//       <td style="border: 1px solid black; padding: 15px; background-color: #e6f7ff;">Colored cell</td>
//       <td style="border: 1px solid black; padding: 15px;">
//         <ul>
//           <li>List item 1</li>
//           <li>List item 2</li>
//         </ul>
//       </td>
//     </tr>
//     <tr>
//       <td style="border: 1px solid black; padding: 15px; height: 100px"></td>
//       <td style="border: 1px solid black; padding: 15px;"></td>
//       <td style="border: 1px solid black; padding: 15px;"></td>
//     </tr>
//   </table>
// `;
//
// For 2, copy the html you want to the clipboard. Then run the following in a
// js console:
//
// const html = '<paste>';
// navigator.clipboard.write([new ClipboardItem({ "text/html": new Blob([html], { type: "text/html" }), "text/plain": new Blob([html], { type: "text/plain" })})]);

/**
 * Parses the provided HTML text, extracts any tables, and builds whiteboard
 * JSON objects for each table found. The tables are positioned starting at
 * (placeAtX, placeAtY) and stacked vertically with a small gap.
 *
 * @param htmlText The HTML text to parse for tables
 * @param placeAtX The X coordinate to place the first table
 * @param placeAtY The Y coordinate to place the first table
 * @param placeAtZ The Z index to assign to the tables
 * @returns An object mapping table IDs to their corresponding whiteboard JSON configurations
 */
export function buildWhiteboardsJSONFromHTML(
  htmlText,
  placeAtX,
  placeAtY,
  placeAtZ
) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlText, 'text/html');

  let nextTableY = placeAtY;
  const tableObjects = {};

  Array.from(doc.querySelectorAll('table')).forEach(table => {
    const tableConfig = buildWhiteboardJSONForTable(
      table,
      placeAtX,
      nextTableY,
      placeAtZ
    );

    tableObjects[tableConfig.id] = tableConfig;

    nextTableY += tableConfig.height + 20;
  });

  return tableObjects;
}

/**
 * Builds a whiteboard JSON configuration for a single HTML table element.
 */
function buildWhiteboardJSONForTable(
  table: HTMLTableElement,
  placeAtX: number,
  placeAtY: number,
  placeAtZ: number
) {
  const rows = Array.from(table.querySelectorAll('tr'));

  if (rows.length === 0) {
    return;
  }

  const cellMatrix = calculateCellMatrix(rows);

  const effectiveNumRows = cellMatrix.filter(row =>
    row.some(cell => cell)
  ).length;

  const effectiveNumColumns = cellMatrix.reduce(
    (max, row) => Math.max(max, row.length),
    0
  );

  // Measure the dimensions of the table
  const { colWidths, rowHeights } = measureTableDimensions(table);

  const tableConfig: any = {
    id: randomId(),
    type: 'table',
    z: placeAtZ,
    textContent: '', // No title for the table
    left: placeAtX,
    top: placeAtY,
    width: colWidths.reduce((total, width) => total + width, 0),
    height: rowHeights.reduce((total, height) => total + height, 0),
    scaleX: 1,
    scaleY: 1,
    numColumns: effectiveNumColumns,
    numRows: effectiveNumRows,
    textFields: {},
  };

  // Set column widths
  colWidths.forEach((width, index) => {
    tableConfig[`col_${index}_width`] = width;
  });

  // Set row heights
  rowHeights.forEach((height, index) => {
    tableConfig[`row_${index}_height`] = height;
  });

  let rowIndex = 0;

  rows.forEach(row => {
    const cells = Array.from(row.querySelectorAll('td,th'));

    let colIndex = 0;

    cells.forEach((cell: HTMLTableCellElement) => {
      applyJsonForCell(cell, tableConfig, rowIndex, colIndex);
      colIndex += cellMatrix[rowIndex][colIndex];
    });

    rowIndex += 1;

    while (cellMatrix[rowIndex]?.[0] === -1) {
      rowIndex += 1;
    }
  });

  return tableConfig;
}

/**
 * Calculates the grid layout and effective grid size accounting
 * for rowspan and colspan. Each grid location will record either how many
 * columns it spans, or that it's part of some other merged cell.
 */
function calculateCellMatrix(rows: HTMLTableRowElement[]) {
  const cellMatrix: number[][] = [];

  rows.forEach((row, rowIndex) => {
    const cells = Array.from(row.querySelectorAll('td,th'));
    let colIndex = 0;

    cells.forEach(cell => {
      // Find the next available column position
      while (cellMatrix[rowIndex]?.[colIndex]) {
        colIndex++;
      }

      const colSpan = parseInt(cell.getAttribute('colspan') || '1', 10);
      const rowSpan = parseInt(cell.getAttribute('rowspan') || '1', 10);

      // Mark cells as occupied in the matrix
      for (let r = 0; r < rowSpan; r++) {
        for (let c = 0; c < colSpan; c++) {
          cellMatrix[rowIndex + r] ||= [];
          // -1 signifies it's part of a merged cell. The parent
          // will have the colSpan saved on it.
          cellMatrix[rowIndex + r][colIndex + c] = -1;
        }
      }

      cellMatrix[rowIndex] ||= [];
      cellMatrix[rowIndex][colIndex] = colSpan;

      colIndex += colSpan;
    });
  });

  return cellMatrix;
}

/**
 * Measures the dimensions of an HTML table and returns arrays of column widths
 * and row heights. It does this by rendering a hidden copy of the table in the
 * DOM and inspecting the sizes of each cell.
 *
 * @param tableElement The HTML table element to measure
 * @returns An object containing arrays of column widths and row heights
 */
function measureTableDimensions(tableElement: HTMLTableElement): {
  colWidths: number[];
  rowHeights: number[];
} {
  // Create a temporary container to render the table for measurement
  const container = document.createElement('div');
  container.id = 'table-measurement-container';
  container.style.position = 'absolute';
  container.style.visibility = 'hidden';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  container.style.width = 'auto';
  container.style.height = 'auto';

  // Clone the table to avoid modifying the original
  const tableClone = tableElement.cloneNode(true) as HTMLTableElement;

  // Make sure the table is rendered with its natural dimensions
  tableClone.style.width = 'auto';
  tableClone.style.height = 'auto';
  tableClone.style.tableLayout = 'auto';

  container.appendChild(tableClone);
  document.body.appendChild(container);

  // Initialize arrays with minimum dimensions
  const MIN_COLUMN_WIDTH = 50;
  const MIN_ROW_HEIGHT = 50;
  const colWidths: number[] = [];
  const rowHeights: number[] = [];

  try {
    // Get all rows in the table
    const rows = tableClone.querySelectorAll('tr');

    rows.forEach((row, rowIndex) => {
      const cells = row.querySelectorAll('td, th');
      let colIndex = 0;
      cells.forEach(cell => {
        // Get cell dimensions
        const rect = cell.getBoundingClientRect();
        const width = rect.width;
        const height = rect.height;

        // Handle colspan - distribute width proportionally
        const colSpan = parseInt(cell.getAttribute('colspan') ?? '1', 10);
        if (colSpan === 1) {
          colWidths[colIndex] = Math.max(colWidths[colIndex] ?? 0, width);
        } else {
          // For cells with colspan, distribute width proportionally
          // We'll use a simple approach of dividing equally for now
          const widthPerCol = width / colSpan;
          for (let i = 0; i < colSpan; i++) {
            colWidths[colIndex + i] = Math.max(
              colWidths[colIndex + i] ?? 0,
              widthPerCol
            );
          }
        }

        // Handle rowspan - distribute height proportionally
        const rowSpan = parseInt(cell.getAttribute('rowspan') ?? '1', 10);
        if (rowSpan === 1) {
          rowHeights[rowIndex] = Math.max(rowHeights[rowIndex] ?? 0, height);
        } else {
          // For cells with rowspan, distribute height proportionally
          const heightPerRow = height / rowSpan;
          for (let i = 0; i < rowSpan; i++) {
            rowHeights[rowIndex + i] = Math.max(
              rowHeights[rowIndex + i] ?? 0,
              heightPerRow
            );
          }
        }

        colIndex += colSpan;
      });
    });

    // Round dimensions to integers
    // Add an extra 15% width to columns to account for differences in padding
    // and text rendering. Erring on the larger side helps ensure we don't
    // have odd word wrapping.
    return {
      colWidths: colWidths.map(width =>
        Math.min(Math.max(Math.round(width * 1.15), MIN_COLUMN_WIDTH), 500)
      ),
      rowHeights: rowHeights.map(height =>
        Math.max(Math.round(height), MIN_ROW_HEIGHT)
      ),
    };
  } finally {
    // Clean up - remove the temporary container
    document.body.removeChild(container);
  }
}

/**
 * Calculates all the necessary attributes for the given html cell and adds them
 * to the tableConfig.
 *
 * @param cell The HTML table cell element (td or th)
 * @param tableConfig The whiteboards json for the table to update
 * @param row The row index of the cell
 * @param col The column index of the cell
 */
function applyJsonForCell(
  cell: HTMLTableCellElement,
  tableConfig: { textFields: Record<string, any> },
  row: number,
  col: number
) {
  const cellKey = `cell_${row}_${col}`;

  const cellTextJson = jsonForCellText(cell);
  tableConfig.textFields[cellKey] = cellTextJson;

  // Handle merged cells
  const colspan = parseInt(cell.getAttribute('colspan') || '1', 10);
  const rowspan = parseInt(cell.getAttribute('rowspan') || '1', 10);

  if (colspan > 1 || rowspan > 1) {
    const mergedCellKey = `merged_cell_${row}_${col}_${row + rowspan - 1}_${
      col + colspan - 1
    }`;
    tableConfig[mergedCellKey] = true;
  }

  const bgColor = cell.style.backgroundColor || '#FFFFFF';
  if (bgColor !== '#FFFFFF' && bgColor !== 'transparent') {
    tableConfig[`${cellKey}_fill`] = bgColor;
  }
}

/**
 * Constructxs the textContent portion of a particular cell in a whiteboards
 * table json.
 *
 * @param cell The HTML table cell element (td or th)
 * @returns An object containing the text content and styling for the cell
 */
function jsonForCellText(cell: HTMLTableCellElement) {
  // Extract cell content
  let textContent = '';
  if (cell.innerHTML) {
    // Preserve HTML formatting
    textContent = cell.innerHTML;
  } else {
    textContent = `<p>${cell.textContent || ''}</p>`;
  }

  // Use the editor serializer to clean up the HTML. If we don't do this
  // now, it will be done the first time the cell is clicked, which can
  // cause the content to shift unexpectedly.
  const editor = createEditor();
  editor.children = new HtmlSerializer(undefined, {
    preserveWhitespace: true,
    enableFontFamilyMark: true,
  }).deserialize(textContent).children;
  AhaEditorUtils.Selection.selectAll(editor);
  textContent = new HtmlSerializer(editor).serialize();

  // Handle text alignment
  let textAlign = 'left';
  if (cell.style.textAlign === 'center') {
    textAlign = 'center';
  } else if (cell.style.textAlign === 'right') {
    textAlign = 'right';
  }

  const verticalAlign = normalizeVerticalAlign(cell.style.verticalAlign);

  return {
    fontSize: 14,
    textAlign,
    verticalAlign,
    textContent,
  };
}
